import { Dispatch, SetStateAction } from 'react'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { ColorScheme } from '@tribeplatform/design-system/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { Select } from '@tribeplatform/react-ui-kit/Select'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'
import { NetworkColorScheme } from '@tribeplatform/react-ui-kit/ThemeProvider'

import { useCustomizer, useCustomizerTheme } from '../../hooks/index.js'
import { useCustomizerLogos } from '../../hooks/use-customizer-logos.hook.js'
import { useCustomizerPreview } from '../../hooks/use-customizer-preview.hook.js'
import { CustomizerActionType } from '../../types.js'
import {
  getTranslatedNetworkColorSchemeMode,
  getTranslatedNetworkColorSchemeModeDescription,
} from '../utils.js'

import { ColorSchemeRadioItem } from './ColorSchemeRadioItem.js'

interface Props {
  onSelectColorScheme: Dispatch<SetStateAction<ColorScheme>>
}

export const ThemeMode = ({ onSelectColorScheme }: Props) => {
  const { $t } = useI18n()
  const { Link } = useRouter()
  const {
    appearance: { networkColorScheme },
  } = useCustomizerTheme()

  const { previewDarkMode, setPreviewDarkMode } = useCustomizerPreview()

  const { dispatch } = useCustomizer()
  const { imageIds } = useCustomizerLogos()

  const updateColorThemeSettings = (newValue: NetworkColorScheme) => {
    dispatch({
      type: CustomizerActionType.NEW_UPDATE,
      payload: {
        theme: {
          typography: {
            colorThemeSettings: newValue,
          },
        },
      },
    })
  }

  const availableModes = ['single', 'user-preference'] as const

  return (
    <div className="space-y-4">
      <FormControl.Layout
        name=""
        label={$t({
          defaultMessage: 'Theme mode',
          id: 'Admin.Appearance.ThemeMode',
        })}
        helperText={getTranslatedNetworkColorSchemeModeDescription(
          $t,
          networkColorScheme.mode,
        )}
        className="pb-5"
      >
        <Select
          value={networkColorScheme.mode}
          onChange={value => {
            const newMode: NetworkColorScheme =
              value === 'user-preference'
                ? {
                    light: 'light1',
                    dark: 'dark1',
                    ...networkColorScheme,
                    mode: value,
                  }
                : {
                    default: 'light1',
                    ...networkColorScheme,
                    mode: value,
                  }
            updateColorThemeSettings(newMode)
          }}
        >
          <Select.Button>
            {getTranslatedNetworkColorSchemeMode($t, networkColorScheme.mode)}
          </Select.Button>
          <Select.Items>
            {availableModes.map(mode => (
              <Select.Item key={mode} value={mode}>
                {getTranslatedNetworkColorSchemeMode($t, mode)}
              </Select.Item>
            ))}
          </Select.Items>
        </Select>
      </FormControl.Layout>

      {networkColorScheme.mode === 'single' ? (
        <RadioGroup
          value={networkColorScheme.default}
          onChange={(value: ColorScheme) =>
            updateColorThemeSettings({
              ...networkColorScheme,
              default: value,
            })
          }
          variant="cards"
        >
          <RadioGroup.Items>
            <ColorSchemeRadioItem
              colorScheme="light1"
              onSelect={onSelectColorScheme}
              active={networkColorScheme.default === 'light1'}
            />
            <ColorSchemeRadioItem
              colorScheme="light2"
              onSelect={onSelectColorScheme}
              active={networkColorScheme.default === 'light2'}
            />
            <ColorSchemeRadioItem
              colorScheme="light3"
              onSelect={onSelectColorScheme}
              active={networkColorScheme.default === 'light3'}
            />
            <ColorSchemeRadioItem
              colorScheme="dark1"
              onSelect={onSelectColorScheme}
              active={networkColorScheme.default === 'dark1'}
            />
            <ColorSchemeRadioItem
              colorScheme="dark2"
              onSelect={onSelectColorScheme}
              active={networkColorScheme.default === 'dark2'}
            />
          </RadioGroup.Items>
        </RadioGroup>
      ) : (
        <Tabs.Group
          selectedIndex={previewDarkMode ? 1 : 0}
          onChange={index => setPreviewDarkMode(index === 1)}
        >
          <Tabs.List variant="plain" fullWidth>
            <Tabs.Tab className="whitespace-nowrap" name="light">
              <T id="Generics.Light" defaultMessage="Light" />
            </Tabs.Tab>
            <Tabs.Tab className="whitespace-nowrap" name="dark">
              <T id="Generics.Dark" defaultMessage="Dark" />
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panels>
            <Tabs.Panel>
              <RadioGroup
                value={networkColorScheme.light}
                onChange={(value: ColorScheme) =>
                  updateColorThemeSettings({
                    ...networkColorScheme,
                    light: value,
                  })
                }
                variant="cards"
              >
                <RadioGroup.Items>
                  <ColorSchemeRadioItem
                    colorScheme="light1"
                    onSelect={onSelectColorScheme}
                    active={networkColorScheme.light === 'light1'}
                  />
                  <ColorSchemeRadioItem
                    colorScheme="light2"
                    onSelect={onSelectColorScheme}
                    active={networkColorScheme.light === 'light2'}
                  />
                  <ColorSchemeRadioItem
                    colorScheme="light3"
                    onSelect={onSelectColorScheme}
                    active={networkColorScheme.light === 'light3'}
                  />
                </RadioGroup.Items>
              </RadioGroup>
            </Tabs.Panel>
            <Tabs.Panel>
              <RadioGroup
                value={networkColorScheme.dark}
                onChange={(value: ColorScheme) =>
                  updateColorThemeSettings({
                    ...networkColorScheme,
                    dark: value,
                  })
                }
                variant="cards"
              >
                <RadioGroup.Items>
                  <ColorSchemeRadioItem
                    colorScheme="dark1"
                    onSelect={onSelectColorScheme}
                    active={networkColorScheme.dark === 'dark1'}
                  />
                  <ColorSchemeRadioItem
                    colorScheme="dark2"
                    onSelect={onSelectColorScheme}
                    active={networkColorScheme.dark === 'dark2'}
                  />
                </RadioGroup.Items>
              </RadioGroup>
            </Tabs.Panel>
          </Tabs.Panels>
        </Tabs.Group>
      )}

      {networkColorScheme.mode === 'user-preference' &&
        !imageIds.darkLogoId && (
          <Alert
            status="warning"
            title={$t({
              defaultMessage:
                'We recommend uploading a dark version of your logo to ensure optimal visibility. We couldn’t detect a dark logo uploaded yet.',
              id: 'Admin.Appearance.ThemeMode.DarkLogoWarning',
            })}
          >
            <Alert.Actions>
              <Link href={adminRoutes.design.appearance.logos}>
                <T
                  id="Admin.Appearance.TakeMeThere"
                  defaultMessage="Take me there"
                />
              </Link>
            </Alert.Actions>
          </Alert>
        )}
    </div>
  )
}
